import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useMultiCmt } from '../contexts/MultiCmtContext';
import { DashboardIcon, LibraryIcon, ChatbotIcon, AdminIcon, IncidentIcon, DocumentIcon, FileTextIcon, MapIcon } from '../icons';
import { SettingOutlined, PlusOutlined } from '@ant-design/icons';
import { LayoutDashboard, FolderKanban, Bot, AlertTriangle, FileText, LifeBuoy, Settings, Map, ClipboardList } from 'lucide-react';
import { Tooltip } from 'antd';
import logo from '../assets/logo/CrisisCompassBannerLogo_v0.png';
import { useActiveIncidentCount } from '../hooks/useActiveIncidentCount';
import crisisReportService from '../services/crisisReportService';

function Sidebar({ isMobile = false, onMobileNavClick = () => {} }) {
  const { currentUser } = useAuth();
  const { activeCmt, activeCmtId } = useMultiCmt();
  const navigate = useNavigate();
  const location = useLocation();
  const [isCollapsed, setIsCollapsed] = useState(() => {
    return localStorage.getItem('sidebarCollapsed') === 'true';
  });
  
  // 🔧 SURGICAL FIX: CMT-aware incident count for sidebar badge (current CMT only)
  const { activeIncidentCount, loading } = useActiveIncidentCount(activeCmtId);
  
  // 🚨 Crisis reports submitted count for triage badge
  const [submittedReportsCount, setSubmittedReportsCount] = useState(0);
  const [reportsLoading, setReportsLoading] = useState(true);
  
  // 🐛 DEBUG: Track Sidebar re-renders
  // Debug log removed to prevent console spam
  
  // Simple responsive state for tablet detection
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 🔧 HYBRID TOUCH DETECTION: Touch tablets get 100px, everything else gets 24px
  const getResponsivePadding = () => {
    const isTouchTablet = ('ontouchstart' in window) && (windowWidth >= 768);
    return isTouchTablet ? '100px' : '24px';
  };

  const getResponsiveToggleBottom = () => {
    const isTouchTablet = ('ontouchstart' in window) && (windowWidth >= 768);
    return isTouchTablet ? '100px' : '24px';
  };

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', isCollapsed.toString());
    window.dispatchEvent(new CustomEvent('sidebarToggle', { 
      detail: { isCollapsed } 
    }));
  }, [isCollapsed]);

  // Add CSS styles to prevent manual DOM manipulation issues
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .nav-button {
        transition: all 0.2s ease !important;
      }
      .nav-button:hover {
        background-color: rgba(255, 255, 255, 0.1) !important;
      }
      .nav-button-active {
        background-color: white !important;
        color: #39325b !important;
      }
      .nav-button-active:hover {
        background-color: white !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  // 🚨 Real-time listener for submitted crisis reports count
  useEffect(() => {
    if (!currentUser?.orgId) {
      setSubmittedReportsCount(0);
      setReportsLoading(false);
      return;
    }

    console.log('🚨 Setting up crisis reports count listener for org:', currentUser.orgId);
    setReportsLoading(true);

    const unsubscribe = crisisReportService.getSubmittedCount(
      currentUser.orgId,
      (count, error) => {
        if (error) {
          console.error('🚨 Crisis reports count listener error:', error);
          setSubmittedReportsCount(0);
        } else {
          console.log('🚨 Crisis reports count updated:', count);
          setSubmittedReportsCount(count);
        }
        setReportsLoading(false);
      }
    );

    return () => {
      console.log('🚨 Cleaning up crisis reports count listener');
      unsubscribe();
    };
  }, [currentUser?.orgId]);

  const handleNewIncident = () => {
    handleNavigation('/incidents/new');
  };

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
  };

  const getPlanDisplay = () => {
    if (currentUser?.isDeveloper) return 'Developer View';
    if (currentUser?.subscription?.tier === 'enterprise') return 'Enterprise Pro Plan';
    if (currentUser?.subscription?.tier === 'business') return 'Business Pro Plan';
    return null;
  };

  const isActive = (path) => {
    return location.pathname === path || location.pathname.startsWith(`${path}/`);
  };

  // Mobile-aware navigation function
  const handleNavigation = (path) => {
    navigate(path);
    if (isMobile) {
      onMobileNavClick(); // Close mobile menu
    }
  };

  const navItems = [
    { 
      path: '/dashboard', 
      label: 'Dashboard', 
      icon: <DashboardIcon />,
      lucideIcon: <LayoutDashboard size={26} color="white" />,
      tooltip: 'View your crisis management dashboard with key metrics and integrated situational awareness map'
    },
    { 
      path: '/my-crisis-resources', 
      label: 'My Crisis Resources', 
      icon: <LibraryIcon />,
      lucideIcon: <FolderKanban size={26} color="white" />,
      tooltip: 'Access your crisis management templates, plans, and resources'
    },
    { 
      path: '/ai-crisis-adviser', 
      label: 'AI Crisis Adviser', 
      icon: <ChatbotIcon />,
      lucideIcon: <Bot size={26} color="white" />,
      tooltip: 'Get AI-powered guidance and recommendations for crisis management'
    },
    { 
      path: '/incidents', 
      label: 'Incidents', 
      icon: <IncidentIcon />,
      lucideIcon: <AlertTriangle size={26} color="white" />,
      tooltip: 'Manage active crisis incidents'
    },
    { 
      path: '/reports', 
      label: 'Reports (Triage)', 
      icon: <IncidentIcon />,
      lucideIcon: <ClipboardList size={26} color="white" />,
      tooltip: 'Triage crisis reports submitted from mobile app'
    },
    { 
      path: '/documents', 
      label: 'Documents', 
      icon: <DocumentIcon />,
      lucideIcon: <FileText size={26} color="white" />,
      tooltip: 'Access organisation level documents and files'
    },
    { 
      path: '/help', 
      label: 'Help and Support', 
      icon: <FileTextIcon />,
      lucideIcon: <LifeBuoy size={26} color="white" />,
      tooltip: 'Get help, view guides, and access support resources'
    },
    { 
      path: '/admin', 
      label: 'Admin and Settings', 
      icon: <AdminIcon />, 
      lucideIcon: <Settings size={26} color="white" />,
      adminOnly: true,
      tooltip: 'Manage system settings, users, and organisation configuration'
    },
  ];

  const filteredNavItems = navItems.filter(item => !item.adminOnly || currentUser?.isAdmin);

  // If collapsed, show icon navigation
  if (isCollapsed) {
    return (
      <aside 
        className="sidebar-collapsed"
        style={{ 
          width: '70px',
          height: '100vh',
          minHeight: '100vh',
          backgroundColor: 'rgb(18, 28, 45)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          flexShrink: 0,
          transition: 'width 0.3s ease',
          borderRight: '1px solid #e6e6e6',
          zIndex: 9999,
          opacity: 1,
          filter: 'none',
          boxShadow: 'none'
        }}
      >
        {/* Collapsed Logo */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center',
          padding: '16px 0',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          marginBottom: '16px'
        }}>
          <Tooltip 
            title="Visit CrisisCompass website" 
            placement="right" 
            mouseEnterDelay={0.2}
          >
            <a
              href="https://crisiscompass.com.au"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                width: '44px',
                height: '44px',
                overflow: 'hidden',
                borderRadius: '8px'
              }}
            >
              <img 
                src="/assets/logo/CrisisCompass_Logo_Image_Only.png"
                alt="CrisisCompass Icon" 
                style={{ 
                  height: '44px',
                  width: '44px',
                  transform: 'scale(4.5) translateX(-0.5px)',
                  objectFit: 'contain',
                  pointerEvents: 'none'
                }}
              />
            </a>
          </Tooltip>
        </div>

        {/* Navigation Icons */}
        <nav style={{ 
          flexGrow: 1, 
          paddingTop: '0px', 
          paddingBottom: '16px', 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '8px', 
          paddingLeft: '10px', 
          paddingRight: '10px' 
        }}>
          {filteredNavItems.map((item) => (
            <Tooltip 
              key={item.path}
              title={item.tooltip}
              placement="right"
              mouseEnterDelay={0.2}
            >
              <button
                onClick={() => handleNavigation(item.path)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '50px',
                  height: '50px',
                  backgroundColor: isActive(item.path) ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
                  color: 'white',
                  border: '0',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!isActive(item.path)) {
                    e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive(item.path)) {
                    e.target.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item.lucideIcon}
                {item.path === '/incidents' && !loading && activeIncidentCount > 0 && (
                  <Tooltip title={`${activeIncidentCount} active incident${activeIncidentCount === 1 ? '' : 's'} in ${activeCmt?.name || 'current CMT'}`} placement="right">
                    <span 
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        backgroundColor: '#ff4d4f',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '1px 4px',
                        fontSize: '0.6rem',
                        fontWeight: 'bold',
                        minWidth: '14px',
                        textAlign: 'center',
                        lineHeight: '1.2'
                      }}
                    >
                      {activeIncidentCount > 9 ? '9+' : activeIncidentCount}
                    </span>
                  </Tooltip>
                )}
                {item.path === '/reports' && !reportsLoading && submittedReportsCount > 0 && (
                  <Tooltip title={`${submittedReportsCount} crisis report${submittedReportsCount === 1 ? '' : 's'} need${submittedReportsCount === 1 ? 's' : ''} triage`} placement="right">
                    <span 
                      style={{
                        position: 'absolute',
                        top: '4px',
                        right: '4px',
                        backgroundColor: '#ff4d4f',
                        color: 'white',
                        borderRadius: '8px',
                        padding: '1px 4px',
                        fontSize: '0.6rem',
                        fontWeight: 'bold',
                        minWidth: '14px',
                        textAlign: 'center',
                        lineHeight: '1.2'
                      }}
                    >
                      {submittedReportsCount > 9 ? '9+' : submittedReportsCount}
                    </span>
                  </Tooltip>
                )}
              </button>
            </Tooltip>
          ))}
        </nav>
        
        {/* Toggle Button */}
        <button
          className="sidebar-toggle-button"
          onClick={toggleSidebar}
          style={{
            position: 'absolute',
            bottom: getResponsiveToggleBottom(), // 🔧 RESPONSIVE FIX: Desktop 24px, Tablet 100px
            left: '50%',
            transform: 'translateX(-50%)',
            width: '40px',
            height: '40px',
            backgroundColor: 'rgb(18, 28, 45)',
            border: '2px solid white',
            borderRadius: '4px',
            color: 'white',
            fontSize: '39px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: '1',
            padding: '0',
            margin: '0',
            zIndex: 1001,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease'
          }}
        >
          <span style={{ transform: 'translateY(-6px)' }}>»</span>
        </button>
      </aside>
    );
  }

  // Expanded sidebar 
  return (
    <aside 
      className="sidebar-expanded"
      style={{ 
        width: '252px',
        // 🔧 FLEXBOX FIX: Proper height and scroll handling
        height: '100vh',
        backgroundColor: 'rgb(18, 28, 45)',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        flexShrink: 0,
        transition: 'width 0.3s ease',
        zIndex: 9999,
        opacity: 1,
        filter: 'none',
        boxShadow: 'none',
        // 🔧 FLEXBOX FIX: Enable scrolling when content overflows
        overflowY: 'auto'
      }}
    >

      {/* Logo Area */}
      <a
        href="https://crisiscompass.com.au"
        target="_blank"
        rel="noopener noreferrer"
        style={{
          backgroundColor: 'white',
          margin: '16px',
          borderRadius: '8px',
          border: '1px solid #e6e6e6',
          padding: '16px',
          textDecoration: 'none',
          color: 'inherit',
          display: 'block',
          cursor: 'pointer'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '8px' }}>
          <img 
            src={logo}
            alt="Crisis Compass Logo" 
            style={{ 
              width: '115%', 
              height: 'auto',
              maxHeight: '66px',
              objectFit: 'contain',
              pointerEvents: 'none'
            }}
          />
        </div>
        {getPlanDisplay() && (
          <small style={{ fontSize: '0.75rem', color: '#6c757d' }}>
            {getPlanDisplay()}
          </small>
        )}
      </a>
      
      {/* Navigation Links */}
      <nav style={{ 
        // 🔧 TABLET FIX: Compress navigation + make scrollable to give buttons more space
        paddingTop: '8px', 
        paddingBottom: '8px', 
        display: 'flex', 
        flexDirection: 'column', 
        gap: '4px', // Reduced from 8px
        paddingLeft: '12px', 
        paddingRight: '12px',
        maxHeight: '400px', // Limit navigation height
        overflowY: 'auto' // Make scrollable if needed
      }}>
        {filteredNavItems.map((item) => (
          <button
            key={item.path}
            onClick={() => handleNavigation(item.path)}
            className={`nav-button ${isActive(item.path) ? 'nav-button-active' : ''}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              paddingLeft: '8px', // Reduced from 12px
              paddingRight: '8px', // Reduced from 12px
              paddingTop: '8px', // Reduced from 12px
              paddingBottom: '8px', // Reduced from 12px
              textDecoration: 'none',
              border: '0',
              borderRadius: '4px',
              backgroundColor: isActive(item.path) ? 'white' : 'transparent',
              color: isActive(item.path) ? '#39325b' : 'white',
              cursor: 'pointer'
            }}
          >
            <span style={{ 
              marginRight: '12px', 
              filter: isActive(item.path) ? 'none' : 'brightness(0) invert(1)' 
            }}>
              {item.icon}
            </span>
            <span style={{ fontWeight: '500', fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
              <Tooltip title={item.tooltip} placement="right">
                <span>{item.label}</span>
              </Tooltip>
              {item.path === '/incidents' && !loading && activeIncidentCount > 0 && (
                <Tooltip title={`${activeIncidentCount} active incident${activeIncidentCount === 1 ? '' : 's'} in ${activeCmt?.name || 'current CMT'}`} placement="right">
                  <span 
                    style={{
                      backgroundColor: '#ff4d4f',
                      color: 'white',
                      borderRadius: '10px',
                      padding: '2px 6px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      display: 'inline-block',
                      marginLeft: '8px',
                      flexShrink: 0,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {activeIncidentCount === 1 ? '1 active' : `${activeIncidentCount} active`}
                  </span>
                </Tooltip>
              )}
              {item.path === '/reports' && !reportsLoading && submittedReportsCount > 0 && (
                <Tooltip title={`${submittedReportsCount} crisis report${submittedReportsCount === 1 ? '' : 's'} need${submittedReportsCount === 1 ? 's' : ''} triage`} placement="right">
                  <span 
                    style={{
                      backgroundColor: '#ff4d4f',
                      color: 'white',
                      borderRadius: '10px',
                      padding: '2px 6px',
                      fontSize: '0.75rem',
                      fontWeight: 'bold',
                      display: 'inline-block',
                      marginLeft: '8px',
                      flexShrink: 0,
                      whiteSpace: 'nowrap'
                    }}
                  >
                    {submittedReportsCount === 1 ? '1 needs triage' : `${submittedReportsCount} need triage`}
                  </span>
                </Tooltip>
              )}
            </span>
          </button>
        ))}
      </nav>

      {/* Quick Actions - Push to bottom with flexbox */}
      <div 
        className="sidebar-quick-actions"
        style={{ 
          marginTop: 'auto', // 🔧 FLEXBOX FIX: Push to bottom of sidebar
          padding: '12px', 
          borderTop: '1px solid rgba(255, 255, 255, 0.2)',
          paddingBottom: getResponsivePadding() // 🔧 RESPONSIVE FIX: Desktop 24px, Tablet 100px
        }}>
        <button 
          onClick={handleNewIncident}
          style={{ 
            backgroundColor: 'white',
            color: '#39325b',
            fontWeight: 'bold',
            padding: '12px',
            borderRadius: '6px',
            transition: 'all 0.2s ease',
            width: '100%',
            marginBottom: '12px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            border: '1px solid white',
            cursor: 'pointer'
          }}
        >
          <PlusOutlined style={{ fontSize: '1.1em', border: '1px solid #39325b', borderRadius: '50%', padding: '2px' }} />
          <span>New Crisis</span>
        </button>

        <button
          onClick={toggleSidebar}
          style={{
            width: '40px',
            height: '40px',
            backgroundColor: 'transparent',
            border: '2px solid white',
            borderRadius: '4px',
            color: 'white',
            fontSize: '39px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            lineHeight: '1',
            padding: '0',
            margin: '0 auto',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            transition: 'all 0.2s ease'
            // 🔧 FLEXBOX FIX: No absolute positioning needed - flexbox handles it
          }}
        >
          <span style={{ transform: 'translateY(-6px)' }}>«</span>
        </button>
      </div>
    </aside>
  );
}

export default Sidebar; 